'use client'

import { useFieldValidations } from 'country-configuration/services/client/useFieldValidations'
import { FormProvider, useForm } from 'form-components'
import { EmailInput } from 'form-components/components/EmailInput/EmailInput'
import { Input } from 'form-components/components/Input/Input'
import { Radio } from 'form-components/components/Radio/Radio'
import { SubmitButton } from 'form-components/components/SubmitButton/SubmitButton'
import { Textarea } from 'form-components/components/Textarea/Textarea'
import { useNavigation } from 'hooks/useNavigation/useNavigation'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { BackButton } from 'legal/components/BackButton/BackButton'
import { Conditions } from 'legal/components/PrivacyRightsRequestForm/Conditions/Conditions'
import {
	FormFieldIds,
	FormFieldNames,
	FormId,
	staticDefaultValues,
} from 'legal/constants/form'
import { usePrivacyRightsOptions } from 'legal/hooks/usePrivacyRightsOptions/usePrivacyRightsOptions'
import { useSubmitPrivacyRightsRequest } from 'legal/hooks/useSubmitPrivacyRightsRequest/useSubmitPrivacyRightsRequest'
import type { RequestFormValues } from 'legal/types/requestFormTypes'
import { SnackbarStatus } from 'notifications/constants/constants'
import { useNotificationsContext } from 'notifications/hooks/useNotificationsContext'

import styles from './Form.module.scss'
import text from 'fukku/styles/texts.module.scss'

export function Form() {
	const {
		email: { regex: emailRegex, maxLength: emailMaxLength },
	} = useFieldValidations()
	const { replace } = useNavigation()
	const { addNotification } = useNotificationsContext()
	const { t } = useLabels()
	const privacyRightsOptions = usePrivacyRightsOptions()
	const { submit } = useSubmitPrivacyRightsRequest()
	const formMethods = useForm({
		defaultValues: staticDefaultValues,
		mode: 'onTouched',
	})

	const { handleSubmit } = formMethods

	const handleOnSubmit = async (values: RequestFormValues) => {
		const isSubmitted = await submit(values)

		if (isSubmitted) {
			replace('/privacy-policy/rights-request-form/success')
			return
		}

		addNotification({
			id: 'form.privacyRights.send.error',
			status: SnackbarStatus.ERROR,
			title: t('form.privacyRights.snackbar.error.title'),
			subtitle: t('form.privacyRights.snackbar.error.subtitle'),
		})
	}

	return (
		<FormProvider {...formMethods}>
			<form
				className={styles.form}
				data-testid={FormId}
				onSubmit={handleSubmit(handleOnSubmit)}
			>
				<Radio
					className={styles.radio}
					id={FormFieldIds.Category}
					name={FormFieldNames.Category}
					data-testid={`${FormId}.${FormFieldNames.Category}`}
					rules={{ required: true }}
					legend={FormFieldNames.Category}
					options={privacyRightsOptions}
				/>
				<h2 className={`${styles.subtitle} ${text.titleM}`}>
					{t('form.privacyRights.personalDetails.bodycopy')}
				</h2>
				<Input
					id={FormFieldIds.FirstName}
					name={FormFieldNames.FirstName}
					data-testid={`${FormId}.${FormFieldNames.FirstName}`}
					label={t('form.privacyRights.name.label')}
					rules={{ required: true }}
				/>
				<Input
					id={FormFieldIds.LastName}
					name={FormFieldNames.LastName}
					data-testid={`${FormId}.${FormFieldNames.LastName}`}
					label={t('form.privacyRights.surname.label')}
					rules={{ required: true }}
				/>
				<EmailInput
					id={FormFieldIds.Email}
					name={FormFieldNames.Email}
					data-testid={`${FormId}.${FormFieldNames.Email}`}
					label={t('form.privacyRights.email.label')}
					rules={{ regex: emailRegex, maxLength: emailMaxLength }}
				/>
				<Input
					id={FormFieldIds.State}
					name={FormFieldNames.State}
					data-testid={`${FormId}.${FormFieldNames.State}`}
					label={t('form.privacyRights.state.label')}
					rules={{ required: true }}
				/>
				<Textarea
					id={FormFieldIds.Message}
					name={FormFieldNames.Message}
					data-testid={`${FormId}.${FormFieldNames.Message}`}
					label={t('form.privacyRights.requestDetails.label')}
					rules={{ required: true }}
				/>
				<Conditions />
				<div className={styles.buttons}>
					<SubmitButton
						id={FormFieldIds.SubmitButton}
						data-testid={`${FormId}.submit`}
					>
						{t('form.privacyRights.send.button')}
					</SubmitButton>
					<BackButton
						data-testid='form.privacyRights.cancel.button'
						aria-label={t(
							'accessibility.form.privacyRights.backToPrivacy.button'
						)}
						isSecondary
						fullWidth
					>
						{t('form.privacyRights.cancel.button')}
					</BackButton>
				</div>
			</form>
		</FormProvider>
	)
}
